import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { AppEnities, CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService, DialogsModule, WindowCloseResult, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import { faAddressCard, faBan, faCopy, faEdit, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';

import { setTitle } from '../../common/utils.lib';
import { Evaluation, EvaluationCopy, EvaluationNew, EvaluationType, IEvaluationCopy } from '../../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { EvaluationService } from '../evaluation.service';
import { EvaluationAddComponent } from '../evaluation-add.component/evaluation-add.component';
import {
  EvaluationRatingScaleListComponent
} from '../evaluation-rating-scale-list.component/evaluation-rating-scale-list.component';
import { EvaluationDomainListComponent } from '../evaluation-domain/evaluation-domain-list.component/evaluation-domain-list.component';
import {
  EvaluationAdditionalQuestionListComponent
} from '../evaluation-additional-question-list.component/evaluation-additional-question-list.component';
import { ModalWindowService } from '../../shared/modal-window.service';
import { EvaluationAddWin } from '../evaluation-add-win/evaluation-add-win';
import { EvaluationRatingScaleListWin } from '../evaluation-rating-scale-list-win/evaluation-rating-scale-list-win';
import { EvaluationDomainQuestionListWin } from '../evaluation-domain/evaluation-domain-question-list-win/evaluation-domain-question-list-win';
import { EvaluationAdditionalQuestionListWin } from '../evaluation-additional-question-list-win/evaluation-additional-question-list-win';


const { EVALUATION } = AppEnities;

@Component({
  selector: 'casi-evaluation-list.component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './evaluation-list.component.html',
  styleUrl: './evaluation-list.component.scss'
})
export class EvaluationListComponent implements OnInit, OnDestroy {

  public view: Observable<Evaluation[]> = new Observable<Evaluation[]>();
  public rawData: Evaluation[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: Evaluation[] = filterBy(this.rawData, this.filter);

  public currentItem: EvaluationType = EvaluationNew.create();

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  public formMode: FormModes = FormModes.NOTSET;

  public canUpdate: boolean = true;
  public canCreate: boolean = true;
  public canDestroy: boolean = true;
  public canView: boolean = true;
  public isSuperUser: boolean = false;

  public editWinOpen: boolean = false;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  private _windowRef: WindowRef | undefined;

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: EvaluationService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private modalWindowService: ModalWindowService,
    private windowService: WindowService,
    private toastr: ToastrService
  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash,
      faBan, faCopy
    );
  }

  public sort: SortDescriptor[] = [
    { field: "organisation_name", dir: "asc" }
  ];

  //#region NG Event handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_update, can_create, can_view, can_destroy } = this.me.allICanDo(EVALUATION);
    this.canUpdate = can_update;
    this.canCreate = can_create;
    this.canView = can_view;
    this.canDestroy = can_destroy;
    this.isSuperUser = this.me.get()?.superuser || false;
    this.dataService.query();

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid methods

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }

  //#endregion

  //#region Public methods

  setActive(dataItem: any): void {
    dataItem.active = !dataItem.active;
    this.logger.debug('setActive:dataItem: ', dataItem);
    this.currentItem = Evaluation.create(dataItem);
    this.saveActive(this.currentItem);
  }

  public onAddClick(titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onAdd...");
    this.currentItem = EvaluationNew.create();
    this.formMode = FormModes.NEW;
    this.showTitle = setTitle(this.formMode, 'EVALUATION');
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  onEditClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = Evaluation.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.showTitle = setTitle(this.formMode, 'EVALUATION');
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  onCopyClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = EvaluationCopy.create(dataItem.id);
    this.formMode = FormModes.COPY;
    this.showTitle = setTitle(this.formMode, 'EVALUATION');
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  public onRatingScaleClick(dataItem: any): void {
    this.logger.debug("onRatingScaleClick: ", dataItem);
    let item = Evaluation.create(dataItem);
    this.logger.debug("onRatingScaleClick:currentItem ", item);
    // this.showTitle = setTitle(this.formMode, 'EVALUATION');
    this.openRatingScaleDialog(item);
  }

  public onDomainClick(dataItem: any): void {
    this.logger.debug("onDomainClick: ", dataItem);
    let item = Evaluation.create(dataItem);
    this.logger.debug("onDomainClick:currentItem ", item);
    this.openDomainDialog(item);
  }

  public onQuestionClick(dataItem: any): void {
    this.logger.debug("onQuestionClick: ", dataItem);
    let item = Evaluation.create(dataItem);
    this.logger.debug("onQuestionClick:currentItem ", item);
    this.openQuestionDialog(item);
  }

  //#endregion

  //#region Private methods

  private setAddEditWinInstanceInputs(dataItem: EvaluationType, mode: FormModes): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.title = setTitle(mode, 'EVALUATION');
      inputs.evaluation = dataItem;
      inputs.mode = mode;
      inputs.existingData = this.rawData;
      this.logger.debug('setAddEditWinInstanceInputs: ', inputs);
    }
  }

  private openAddEditDialog(mode: FormModes, dataItem: EvaluationType, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("openAddEditDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      titleBarContent: titleBar,
      content: EvaluationAddWin,
      minWidth: 320,
      width: 800,
    });

    this.setAddEditWinInstanceInputs(dataItem, mode);

    this._windowRef.content.instance.onSubmit.subscribe((formData: FormGroup) => {
      this.logger.debug('openAddEditDialog:formData: ', formData);
      this.handleClose(formData.value, mode);
    });

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openAddEditDialog:onClose');
      this.closeWindowRef();
    });

  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    this.save(data, mode);
  }

  private save(evaluation: any, mode: FormModes): void {
    this.logger.debug('save...', evaluation, mode);
    if (mode === FormModes.EDIT) {
      this.dataService.update(evaluation.id, evaluation)
        .then(resp => {
          this.logger.debug('save:update:resp: ', resp);
          this.notify("Evaluation Updated");
          this.closeWindowRef();
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('save:update:err: ', err);
          this.notify("Error Updating Evaluation", true);
        });
    } else if (mode === FormModes.NEW) {
      this.dataService.add(evaluation)
        .then(resp => {
          this.logger.debug('save:add:resp: ', resp);
          this.notify("Evaluation Added");
          this.closeWindowRef();
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('save:add:err: ', err);
          this.notify("Error Creating Evaluation", true);
        });
    } else
      this.dataService.copy(evaluation)
        .then(resp => {
          this.logger.debug('save:add:resp: ', resp);
          this.notify("Evaluation Added");
          this.closeWindowRef();
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('save:add:err: ', err);
          this.notify("Error Creating Evaluation", true);
        });
  }

  private saveActive(evaluation: any): void {
    this.logger.debug('saveActive...', evaluation);
    this.dataService.update(evaluation.id, evaluation)
      .then(resp => {
        this.logger.debug('saveActive:update:resp: ', resp);
        this.notify(evaluation.active ? "Evaluation Activated" : "Evaluation Deactivated");
        this.closeWindowRef();
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('saveActive:update:err: ', err);
        this.notify("Error Updating Evaluation", true);
      });
  }

  private setRatingScaleWinInstanceInputs(dataItem: EvaluationType): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.evaluation = dataItem;
      this.logger.debug('setRatingScaleWinInstanceInputs: ', inputs);
    }
  }

  private openRatingScaleDialog(dataItem: Evaluation): void {
    this.logger.debug("openRatingScaleDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      title: 'MANAGE EVALUATION RATING SCALES',
      content: EvaluationRatingScaleListWin,
      minWidth: 320,
      width: 1000,
    });

    this.setRatingScaleWinInstanceInputs(dataItem);

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openStudentDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openStudentDialog:onClose');
      this.closeWindowRef();
    });
  }

  private openDomainDialog(dataItem: Evaluation): void {
    this.logger.debug("openDomainDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      title: 'MANAGE EVALUATION DOMAINS',
      content: EvaluationRatingScaleListWin,
      minWidth: 320,
      width: 1200,
    });

    this.setRatingScaleWinInstanceInputs(dataItem);

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openStudentDialog:result:', r);
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openStudentDialog:onClose');
      this.closeWindowRef();
    });
  }

  private openQuestionDialog(dataItem: Evaluation): void {
    this.logger.debug("openQuestionDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      title: 'MANAGE EVALUATION ADDITIONAL QUESTIONS',
      content: EvaluationAdditionalQuestionListWin,
      minWidth: 320,
      width: 1600,
    });

    this.setRatingScaleWinInstanceInputs(dataItem);

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openStudentDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openStudentDialog:onClose');
      this.closeWindowRef();
    });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }


  private closeWindowRef(): void {
    if (this._windowRef) {
      this.modalWindowService.close(this._windowRef);
      //  this._windowRef.close();
      this._windowRef = undefined;
    }
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  //#endregion

}
